import { effect } from "@preact/signals";
import {
  selectedComposerId,
  selectedPieceKey,
  selectedPiece,
  selectedOrgId,
  setCityFilter,
  clearCityFilter,
  selectedCityId,
  selectedCitySource,
  radiusKm,
  selectedCountry,
  selectedGenre,
  GENRE_OPTIONS,
  viewMode,
  exploreScope,
  exploreEra,
  exploreAlive,
  explorePremiere,
  theme,
  listDirectionExplicit,
  consumeHistoryReplaceHint,
  disabledSources,
  LIVE_SOURCE_KEY,
  timeFrom,
  timeTo,
  timeWindowFromURL,
  timeWindowTouched,
  listDirection,
  anchorISO,
  mapBounds,
  restoreMapViewport,
  showExtras,
  storedShowExtras,
} from "./store";
import { ensureFacets } from "../data/loader";

let skipNextPush = false;

/** Read URL params into state signals on page load (`coldLoad`) and on Back/Forward. */
export function initFromURL({ coldLoad = false } = {}) {
  const params = new URLSearchParams(window.location.search);

  skipNextPush = true;

  const composer = params.get("composer");
  selectedComposerId.value = composer ? parseInt(composer, 10) || null : null;

  // Only the key round-trips through the URL; the label is fetched by the resolver in
  // data/queries.ts. Drop any stale label here so the chip can't show the previous piece's
  // name against the new key while that lookup is in flight (this fires on popstate too).
  const piece = params.get("piece");
  selectedPieceKey.value = piece || null;
  if (selectedPiece.peek()?.key !== piece) selectedPiece.value = null;

  const org = params.get("org");
  selectedOrgId.value = org ? parseInt(org, 10) || null : null;

  // A shared link with a composer/org filter needs the facet lists to *name* the chip.
  if (selectedComposerId.peek() !== null || selectedOrgId.peek() !== null) {
    void ensureFacets().catch(() => {});
  }

  const city = params.get("city");
  if (city) {
    setCityFilter(parseInt(city, 10) || null, "manual");
    // `km` widens the city filter into a radius — only meaningful alongside `city`.
    const km = parseInt(params.get("km") ?? "", 10);
    radiusKm.value = Number.isFinite(km) && km > 0 ? Math.min(km, 1000) : null;
  } else {
    clearCityFilter();
  }

  const country = params.get("country");
  selectedCountry.value = country ?? "";

  const genre = params.get("genre");
  selectedGenre.value =
    genre && GENRE_OPTIONS.some((g) => g.value === genre) ? genre : "";

  // ── Time window. `from`/`to` are ISO dates on the ONE time axis. Legacy links used
  // `yfrom`/`yto` (archive-mode years) — map them onto the same axis.
  const from = params.get("from");
  const to = params.get("to");
  const yfrom = params.get("yfrom");
  const yto = params.get("yto");
  if (from || to) {
    if (from) timeFrom.value = from;
    if (to) timeTo.value = to;
    timeWindowFromURL.value = true;
  } else if (yfrom || yto) {
    const fy = yfrom ? parseInt(yfrom, 10) : NaN;
    const ty = yto ? parseInt(yto, 10) : NaN;
    if (Number.isFinite(fy)) timeFrom.value = `${fy}-01-01`;
    if (Number.isFinite(ty)) timeTo.value = `${ty}-12-31`;
    timeWindowFromURL.value = true;
  }

  // ── Sources. `xsrc` = excluded source keys ("live" or archive slugs). Legacy links:
  // `live=0` excluded the live layer; `arch=0` the whole archive; `lowconf=0` the
  // lower-confidence sources. The archive-wide forms park a placeholder that
  // data/loader.ts expands into explicit keys once the source list arrives.
  const disabled = new Set<string>();
  const xsrc = params.get("xsrc");
  if (xsrc) {
    for (const key of xsrc.split(",")) {
      const k = key.trim();
      if (k) disabled.add(k);
    }
  }
  if (params.get("live") === "0") disabled.add(LIVE_SOURCE_KEY);
  if (params.get("arch") === "0") disabled.add("_archive");
  if (params.get("lowconf") === "0") disabled.add("_probable");
  disabledSources.value = disabled;

  // The opt-in toggle: a link's `extras=` wins, then the reader's remembered setting, which only
  // applies on arrival. On Back/Forward the entry's URL is the state it had.
  const extras = params.get("extras");
  showExtras.value = extras !== null ? extras === "1" : coldLoad && storedShowExtras();

  // ── List direction ("Upcoming" vs "Past"). Only meaningful with an anchor; harmless
  // without one (the query ignores it then).
  listDirection.value = params.get("dir") === "past" ? "backward" : "forward";

  // Legacy `era=archive` links meant "show me the past" — honor the intent.
  if (params.get("era") === "archive") listDirection.value = "backward";

  // A direction stated in the URL is the sharer's deliberate choice — the empty-side
  // auto-switch must not override it.
  listDirectionExplicit.value = params.has("dir") || params.get("era") === "archive";

  const mode = params.get("mode");
  viewMode.value = mode === "explore" ? "explore" : "browse";

  // Explore-only state. (`tab=` from the pre-2026-08-29 tabbed Explore is ignored: the
  // page is one cross-filtered dashboard now, so every old link still lands somewhere sane.)
  const scope = params.get("scope");
  exploreScope.value = scope === "past" || scope === "all" ? scope : "upcoming";
  const xera = parseInt(params.get("xera") ?? "", 10);
  exploreEra.value = Number.isFinite(xera) && xera % 50 === 0 ? xera : null;
  exploreAlive.value = params.get("alive") === "1";
  explorePremiere.value = params.get("premiere") === "1";

  // Read (old links carried it) but never written anymore — theme is a device
  // preference (localStorage), not something to impose through a shared link.
  const t = params.get("theme");
  if (t === "dark" || t === "light") theme.value = t;

  skipNextPush = false;
}

/** Param changes that get history.replaceState instead of pushState: the time window
 *  (slider drags fire dozens of writes per gesture — one filter, not a browsing step). */
const REPLACE_ONLY_PARAMS = new Set(["from", "to"]);

/** Start syncing state signals → URL params. Call once after initFromURL. */
export function startURLSync() {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  // The first write only canonicalizes the load-time URL (legacy params, defaults) —
  // it must never mint a history entry the Back button would step through.
  let firstWrite = true;

  effect(() => {
    // Read all signals to subscribe
    const state = {
      composer: selectedComposerId.value,
      piece: selectedPieceKey.value,
      org: selectedOrgId.value,
      // Popup-sourced city selections are transient (they only narrow the
      // list while the popup is open) — keeping them out of the URL stops a
      // reload from resurrecting them as a manual filter that empties the map.
      city: selectedCitySource.value === "popup" ? null : selectedCityId.value,
      km: selectedCitySource.value === "popup" ? null : radiusKm.value,
      country: selectedCountry.value,
      genre: selectedGenre.value,
      from: timeFrom.value,
      to: timeTo.value,
      windowDeliberate: timeWindowTouched.value || timeWindowFromURL.value,
      disabled: disabledSources.value,
      extras: showExtras.value,
      direction: listDirection.value,
      anchored: anchorISO.value !== null,
      mode: viewMode.value,
      scope: exploreScope.value,
      xera: exploreEra.value,
      alive: exploreAlive.value,
      premiere: explorePremiere.value,
    };

    if (skipNextPush) return;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const params = new URLSearchParams();

      if (state.composer !== null) params.set("composer", String(state.composer));
      if (state.piece !== null) params.set("piece", state.piece);
      if (state.org !== null) params.set("org", String(state.org));
      if (state.city !== null) params.set("city", String(state.city));
      if (state.city !== null && state.km !== null) params.set("km", String(state.km));
      if (state.country) params.set("country", state.country);
      if (state.genre) params.set("genre", state.genre);
      // The window only serializes once it's deliberate — the default full span depends
      // on meta.json and must not be frozen into every copied URL.
      if (state.windowDeliberate) {
        params.set("from", state.from);
        if (state.to) params.set("to", state.to);
      }
      if (state.disabled.size > 0) {
        params.set("xsrc", [...state.disabled].sort().join(","));
      }
      if (state.extras) params.set("extras", "1");
      if (state.anchored && state.direction === "backward") params.set("dir", "past");
      if (state.mode !== "browse") params.set("mode", state.mode);
      if (state.mode === "explore" && state.scope !== "upcoming") params.set("scope", state.scope);
      if (state.xera !== null) params.set("xera", String(state.xera));
      if (state.alive) params.set("alive", "1");
      if (state.premiere) params.set("premiere", "1");

      const search = params.toString();
      const url = search ? `?${search}` : window.location.pathname;

      // Discrete changes PUSH so the browser's Back button walks back through filter and
      // mode states instead of leaving the site (everything used to replace, which folded
      // a whole session into one history entry). Replace when: nothing changed, the write
      // is load-time canonicalization, a programmatic writer hinted it isn't a user step,
      // or only the time window moved (a drag, not a navigation).
      const replaceHinted = consumeHistoryReplaceHint();
      const changed = changedParams(new URLSearchParams(window.location.search), params);
      // The first flush is the load-time canonicalization pass whether or not it changes
      // anything — consume the flag even on the no-change path, or the first real user
      // action would be misfiled as canonicalization and replaced instead of pushed.
      const wasFirst = firstWrite;
      firstWrite = false;
      if (changed.length === 0) return;
      const push =
        !wasFirst && !replaceHinted && changed.some((k) => !REPLACE_ONLY_PARAMS.has(k));
      // The viewport rides in the history state object, never in the URL: a shared link should
      // not carry one reader's camera, and the URL is the shareable surface. See #013.
      const historyState = { mapBounds: mapBounds.peek() };
      if (push) window.history.pushState(historyState, "", url);
      else window.history.replaceState(historyState, "", url);
    }, 300);
  });

  // Handle back/forward navigation
  window.addEventListener("popstate", (event) => {
    initFromURL();
    // Then put the camera back where this entry left it (#013). AFTER initFromURL, so the
    // restore is the last focus request of the turn and wins — though in practice none of the
    // setters initFromURL calls touch the map (resetMapToWorld is only ever fired by a UI
    // component reacting to a click).
    const bbox = (event.state as { mapBounds?: unknown } | null)?.mapBounds;
    if (isBbox(bbox)) restoreMapViewport(bbox);
  });

  // Keep the CURRENT entry's recorded viewport up to date as the reader pans and zooms, so that
  // when a later navigation pushes a new entry this one already remembers the camera it had.
  // Stamping continuously is what makes it race-free: the alternative — reading mapBounds at push
  // time — is a coin flip, because the deliberate zoom-out fires on the click and the URL flush is
  // debounced 300ms behind it, while the camera animation settles at ~700ms.
  effect(() => {
    const bbox = mapBounds.value;
    if (!isBbox(bbox)) return;
    const prev = (window.history.state as Record<string, unknown> | null) ?? {};
    // replaceState with an empty URL keeps the address bar exactly as-is.
    window.history.replaceState({ ...prev, mapBounds: bbox }, "");
  });
}

/** Keys whose values differ between two URLSearchParams (present-vs-absent counts). */
function changedParams(a: URLSearchParams, b: URLSearchParams): string[] {
  const keys = new Set([...a.keys(), ...b.keys()]);
  return [...keys].filter((k) => a.get(k) !== b.get(k));
}


/** A stored viewport is only usable if it really is [w, s, e, n] of finite numbers — history
 *  state survives reloads and can predate a deploy that changed its shape. */
function isBbox(v: unknown): v is [number, number, number, number] {
  return (
    Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === "number" && Number.isFinite(n))
  );
}
